import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { AppConfig, JWT } from '../../config';
import axios from 'axios';
import html2pdf from 'html2pdf.js/src/index.js'
import moment from 'moment'
import SiteSurveyPdf from '../../config/SiteSurveyPdf.txt';
import InstallationPdf from '../../config/InstallationPdf.txt';
import CCTPdf from '../../config/CCR.txt';

import { CountryCode } from '../../config/CountryCode';
import { siteSurveyColumn, installationColumn } from '../../config/ReportColumnPdf';
import { Translate as t } from '../../config/Translate';
import produthistoryreport from '../../config/producthistoryreport.txt';
import { resolveFileUrl } from '../../utils/filePreview';
import { escapeHtmlAttr, escapeHtml } from '../../utils/htmlEscape';
import { runPdfExport } from '../../lib/pdfExport';

// ─── 모듈 스코프 순수 유틸 ────────────────────────────────────────────────────

const getCountryName = (code) => {
  if (CountryCode.filter(item => code == item.code).length > 0) {
    return CountryCode.find(item => code == item.code).title;
  } else {
    return code;
  }
}

const getCountryCode = (title) => {
  if (CountryCode.filter(item => title == item.title).length > 0) {
    return CountryCode.find(item => title == item.title).code;
  } else {
    return title;
  }
}

const convertCodeData = (columnName, inputData) => {
  if (columnName == "siteCountry")
    return getCountryName(inputData);
  else if (columnName == "scheduleDate" || columnName == "trainingDate" || columnName == "deliveryDate" || columnName == "installationDate" || columnName == "createDate")
    return moment.utc(inputData == "0" ? 0 : inputData).local().format("YYYY-MM-DD");
  else if (columnName == "") {
    return "";
  }
  else {
    return inputData;
  }
}

// 미충족 placeholder({<prefix>.<token>}) 빈칸 폴백 — literal 누출 방지
export const blankUnfilledTokens = (html, prefix) => {
  if (!html) return html;
  return html.replace(new RegExp('\\{' + prefix + '\\.[A-Za-z0-9_-]+\\}', 'g'), '');
};

// install 보고서: SiteReducer.site 데이터를 install 컬럼 키로 병합 (BUG-138)
export const mergeSiteIntoInstallInfo = (infos, site) => {
  const s = site || {};
  return {
    ...infos,
    siteName: s.sitename ?? infos.siteName,
    siteSpecialty: s.speciality ?? infos.siteSpecialty,
    siteTel: s.tel ?? infos.siteTel,
    siteAddress: s.address ?? infos.siteAddress,
    siteZipCode: s.zipcode ?? infos.siteZipCode,
    siteCountry: s.country ?? infos.siteCountry,
    customerFirstName: s.customerFirstName ?? infos.customerFirstName,
    customerLastName: s.customerLastName ?? infos.customerLastName,
    customerEmail: s.customerEmail ?? infos.customerEmail,
    customerHp: s.customerHp ?? infos.customerHp,
  };
};

// install 보고서: ProductReducer.product 데이터를 install 컬럼 키로 비파괴 병합 (BUG-138)
// version/networkStaticIp 는 product.extrainfo, model/option/sn 은 product 본체. product 값 없으면 infos 보존.
export const mergeProductIntoInstallInfo = (infos, product) => {
  const p = product || {};
  const ex = p.extrainfo || {};
  return {
    ...infos,
    model: p.model ?? infos.model,
    option: p.codes ?? infos.option,
    sn: p.sn ?? infos.sn,
    networkStaticIp: ex.networkstaticip ?? infos.networkStaticIp,
    rayIoVersion: ex.verRayio ?? infos.rayIoVersion,
    rayScanVersion: ex.verRaycan ?? infos.rayScanVersion,
    smartdentVersion: ex.verSmartDent ?? infos.smartdentVersion,
    rayServerVersion: ex.verRayServer ?? infos.rayServerVersion,
    ondemandVersion: ex.verOndemand3D ?? infos.ondemandVersion,
  };
};

// ─── buildInstallReportHtml — 순수 함수 (BUG-138) ───────────────────────────
/**
 * 설치보고서 HTML 템플릿에 실데이터를 치환하여 완성된 HTML 문자열을 반환한다.
 *
 * @param {string} templateText    InstallationPdf.txt 원문
 * @param {object} infos           설치보고서 데이터
 * @param {Array}  installationDrawing  getReportAttachs() 결과 배열
 * @param {Function} tFn           번역 함수 (i18n t)
 * @returns {string} 치환 완료된 HTML 문자열
 */
export const buildInstallReportHtml = (templateText, infos, installationDrawing, tFn) => {
  if (!templateText) return '';   // fetch 실패 등으로 templateText 가 falsy 일 때 TypeError 방지
  let html = templateText;

  installationColumn.map((x) => {
    // 라벨 placeholder 치환: {title} → t(title)
    // i18n 값을 HTML 템플릿에 직접 삽입 — XSS 패턴(script/on*=/javascript:)은 yarn sync:i18n --strict-html --apply 로 opt-in 차단 가능 (IMP-250). render 시점 escape 미적용.
    html = html.replaceAll('{' + x.title + '}', tFn(x.title));

    // 데이터 placeholder 치환: {ir.<column>} → infos 실데이터
    if (x.column in infos) {
      if (x.column == "customerFirstName") {
        html = html.replace('{ir.' + x.column + '}', escapeHtml([infos.customerFirstName, infos.customerLastName].filter(Boolean).join(' ')));
      } else {
        html = html.replace('{ir.' + x.column + '}', escapeHtml(convertCodeData(x.column, infos[x.column])));
      }
    }

    // pacsList 특수 블록
    if (x.column == 'pacsList') {
      if (infos.pacsList && infos.pacsList.length > 0) {
        //type, title, ip, port — 타입(mwl/dicom/scp)별 0-base 인덱스로 치환
        const pacsTypeIdx = {};
        infos.pacsList.forEach((pacs) => {
          const ptype = pacs.type;
          const idx = pacsTypeIdx[ptype] ?? 0;
          html = html.replace('{ir.pacs-' + ptype + '-title' + idx + '}', escapeHtml(pacs.title));
          html = html.replace('{ir.pacs-' + ptype + '-ip' + idx + '}', escapeHtml(pacs.ip));
          html = html.replace('{ir.pacs-' + ptype + '-port' + idx + '}', escapeHtml(pacs.port));
          pacsTypeIdx[ptype] = idx + 1;
        });
      }
      for (let i = 0; i < 3; i++) {
        html = html.replace('{ir.pacs-mwl-title' + i + '}', '');
        html = html.replace('{ir.pacs-mwl-ip' + i + '}', '');
        html = html.replace('{ir.pacs-mwl-port' + i + '}', '');
        html = html.replace('{ir.pacs-dicom-title' + i + '}', '');
        html = html.replace('{ir.pacs-dicom-ip' + i + '}', '');
        html = html.replace('{ir.pacs-dicom-port' + i + '}', '');
        html = html.replace('{ir.pacs-scp-title' + i + '}', '');
        html = html.replace('{ir.pacs-scp-ip' + i + '}', '');
        html = html.replace('{ir.pacs-scp-port' + i + '}', '');
      }
    }

    // packing 특수 블록
    if (x.column == "packing") {
      if (installationDrawing.length > 0) {
        const shockImage = [];
        const tiltImage = [];
        installationDrawing.map((s) => {
          if (s.type == "shock") {
            shockImage.push(s.path)
            html = html.replace('{ir.shock}', shockImage);
          } else if (s.type == "tilt") {
            tiltImage.push(s.path)
            html = html.replace('{ir.tilt}', tiltImage);
          }
        })
      }
      html = html.replace('{ir.shock}', '');
      html = html.replace('{ir.tilt}', '');
    }

    // image quality 특수 블록
    if (x.column == "imagequality") {
      if (installationDrawing.length > 0) {
        const panoImage = [];
        const cbctImage = [];
        const cephImage = [];
        installationDrawing.map((img) => {
          if (img.type == "pano") {
            panoImage.push(img.path)
            html = html.replace('{ir.iq_pano}', panoImage);
          } else if (img.type == "ct") {
            cbctImage.push(img.path)
            html = html.replace('{ir.iq_cbct}', cbctImage);
          } else if (img.type == "ceph") {
            cephImage.push(img.path)
            html = html.replace('{ir.iq_ceph}', cephImage);
          }
        })
      }
      html = html.replace('{ir.iq_pano}', '');
      html = html.replace('{ir.iq_cbct}', '');
      html = html.replace('{ir.iq_ceph}', '');
    }

    // signature 특수 블록
    if (x.column == "signatures") {
      if (infos.signatures && infos.signatures.length > 0) {
        infos.signatures.map((s) => {
          if (s.type == "customer") {
            html = html.replace('{ir.sig_customer}', "<img style='width:324px;height:123px;' class='img-fluid' alt='' crossorigin='anonymous' src='" + escapeHtmlAttr(s.path) + "'/>")
            html = html.replace('{ir.sig_customer_updateDate}', convertCodeData('createDate', s.createDate));
          } else {
            html = html.replace('{ir.sig_installer}', "<img style='width:324px;height:123px;' class='img-fluid' alt='' crossorigin='anonymous' src='" + escapeHtmlAttr(s.path) + "'/>")
            html = html.replace('{ir.sig_installer_updateDate}', convertCodeData('createDate', s.createDate));
          }
        });
      }
      html = html.replace('{ir.sig_customer}', '');
      html = html.replace('{ir.sig_installer}', '');
      html = html.replace('{ir.sig_customer_updateDate}', '');
      html = html.replace('{ir.sig_installer_updateDate}', '');
    }

    // Regulatory Document 특수 블록
    if (x.column == "regulatorydocument") {
      if (installationDrawing.length > 0) {
        const docImage = [];
        installationDrawing.map((r) =>
          r.type == "doc" &&
          docImage.push(r.path)
        )
        html = html.replace('{ir.reg_doc}', docImage);
      }
      html = html.replace('{ir.reg_doc}', '');
    }
  });

  // 정적 라벨 보존 (루프 밖 1회)
  html = html.replaceAll('{company}', 'Company')
    .replaceAll('{rayserverversion}', 'RAYServer Version')
    .replaceAll('{ondemand3dversion}', 'OnDemand3D Version')
    .replaceAll('{application}', 'Application');

  // 미충족 {ir.X} 빈칸 폴백 — 잔여 placeholder 누출 방지
  html = blankUnfilledTokens(html, 'ir');

  return html;
}

// ─── buildSiteSurveyReportHtml — 순수 함수 ───────────────────────────────────
// NOTE: previousUnits Extra/Intra 블록은 IMP-294 에서 for-in+`i++`(let 스코프라 동작 영향 없던
//       dead code) → forEach 0-base 인덱스로 정리하고, IntraOral 에 length>0 가드를 추가해
//       Extra 와 대칭화함. 출력 동치 보존(BUG-138 PR#346 escape 대칭화 결과 유지).
export const buildSiteSurveyReportHtml = (templateText, infos, surveyDrawing, tFn) => {
  if (!templateText) return '';
  let siteSurveyHtmlString = templateText;
  siteSurveyColumn.map((x) => {
    siteSurveyHtmlString = siteSurveyHtmlString.replaceAll('{' + x.title + '}', tFn(x.title));
    if (x.column in infos) {
      if (x.column == "customerFirstName") {
        siteSurveyHtmlString = siteSurveyHtmlString.replace('{ss.' + x.column + '}', escapeHtml([infos.customerFirstName, infos.customerLastName].filter(Boolean).join(' ')));
      } else if (x.column == "additionalRioSensorSize1") {
        const sensorsize1 = (infos.additionalRioSensorSize1 == "" || infos.additionalRioSensorSize1 == null || infos.additionalRioSensorSize1 == "0") ? "" : "(Size1 / " + infos.additionalRioSensorSize1 + " EA)";
        const sensorsize2 = (infos.additionalRioSensorSize2 == "" || infos.additionalRioSensorSize2 == null || infos.additionalRioSensorSize2 == "0") ? "" : "(Size2 / " + infos.additionalRioSensorSize2 + " EA)";
        const sensortotal = (sensorsize1 == "" ? "" : sensorsize1 + ", ") + (sensorsize2 == "" ? "" : sensorsize2);
        siteSurveyHtmlString = siteSurveyHtmlString.replace('{ss.riosensorQty}', sensorsize1 + sensorsize2);
        siteSurveyHtmlString = siteSurveyHtmlString.replace('{ss.riosensorSizeTotal}', sensortotal);
      } else if (x.column == "additionalRioScanStandard") {
        const scanstandard = (infos.additionalRioScanStandard == "" || infos.additionalRioScanStandard == null || infos.additionalRioScanStandard == "0") ? "" : "(Standard / " + infos.additionalRioScanStandard + " EA)";
        const scanocclusal = (infos.additionalRioScanOcctusal == "" || infos.additionalRioScanOcctusal == null || infos.additionalRioScanOcctusal == "0") ? "" : "(Occlusal / " + infos.additionalRioScanOcctusal + " EA)";
        const scantotal = (scanstandard == "" ? "" : scanstandard + ", ") + (scanocclusal == "" ? "" : scanocclusal);
        siteSurveyHtmlString = siteSurveyHtmlString.replace('{ss.rioscanQty}', scanstandard + scanocclusal);
        siteSurveyHtmlString = siteSurveyHtmlString.replace('{ss.rioscanTotal}', scantotal);
      } else {
        siteSurveyHtmlString = siteSurveyHtmlString.replace('{ss.' + x.column + '}', escapeHtml(convertCodeData(x.column, infos[x.column])));
      }

      if (x.column == 'previousUnitsExtraOralList') {
        if (infos.previousUnitsExtraOralList.length > 0) {
          infos.previousUnitsExtraOralList.forEach((unit, i) => {
            siteSurveyHtmlString = siteSurveyHtmlString.replace('{ss.pUnitsExtraOralBrand' + i + '}', escapeHtml(unit.brand));
            siteSurveyHtmlString = siteSurveyHtmlString.replace('{ss.pUnitsExtraOralProduct' + i + '}', escapeHtml(unit.product));
          });
        }
        for (let i = 0; i < 3; i++) {
          siteSurveyHtmlString = siteSurveyHtmlString.replace('{ss.pUnitsExtraOralBrand' + i + '}', '');
          siteSurveyHtmlString = siteSurveyHtmlString.replace('{ss.pUnitsExtraOralProduct' + i + '}', '');
        }
      }

      if (x.column == 'previousUnitsIntraOralList') {
        if (infos.previousUnitsIntraOralList.length > 0) {
          infos.previousUnitsIntraOralList.forEach((unit, i) => {
            siteSurveyHtmlString = siteSurveyHtmlString.replace('{ss.pUnitsIntraOralBrand' + i + '}', escapeHtml(unit.brand));
            siteSurveyHtmlString = siteSurveyHtmlString.replace('{ss.pUnitsIntraOralProduct' + i + '}', escapeHtml(unit.product));
          });
        }
        for (let i = 0; i < 3; i++) {
          siteSurveyHtmlString = siteSurveyHtmlString.replace('{ss.pUnitsIntraOralBrand' + i + '}', '');
          siteSurveyHtmlString = siteSurveyHtmlString.replace('{ss.pUnitsIntraOralProduct' + i + '}', '');
        }
      }

      if (x.column == 'monitorList') {
        let monitorInch = '';
        let monitorQuantity = '';
        for (const m in infos.monitorList) {
          monitorInch += infos.monitorList[m].inch + ' ';
          monitorQuantity += infos.monitorList[m].quantity + ' ';
        }
        siteSurveyHtmlString = siteSurveyHtmlString.replace('{ss.monitorOtherInches}', escapeHtml(monitorInch));
        siteSurveyHtmlString = siteSurveyHtmlString.replace('{ss.monitorOtherQuantity}', escapeHtml(monitorQuantity));
      }

      if (x.column == "attachments") {
        if (infos.attachments.length > 0) {
          siteSurveyHtmlString = siteSurveyHtmlString.replace('{sss.attachments}', surveyDrawing.join("<br />"));
        }
      }
    }
  });
  siteSurveyHtmlString = blankUnfilledTokens(siteSurveyHtmlString, 'ss');
  siteSurveyHtmlString = blankUnfilledTokens(siteSurveyHtmlString, 'sss');
  return siteSurveyHtmlString;
};

// ─── ExportPdfReport 컴포넌트 ─────────────────────────────────────────────────

export const ExportPdfReport = ({ reportType, infos, css, auto = true }) => {
  // survey 정보 가져와서 Document 생성
  // button click 으로 PDFDownloadLink 생성
  // const [ t, i18n ] = useTranslation();

  const reportSite = useSelector((state) => state.SiteReducer?.site) || {};
  const reportProduct = useSelector((state) => state.ProductReducer?.product) || {};

  const installGeneratePDF = async () => {
    const installationDrawing = await getReportAttachs(infos);
    const text = await fetch(InstallationPdf).then(r => r.text());
    const merged = mergeProductIntoInstallInfo(mergeSiteIntoInstallInfo(infos, reportSite), reportProduct);
    const installHtmlString = buildInstallReportHtml(text, merged, installationDrawing, t);
    const ctry = merged.siteCountry ? getCountryCode(merged.siteCountry) : '';
    const filename = "IR-" + ctry + "-" + (merged.sn || '') + "-" + (merged.siteName || '') + "-" + moment.utc(merged.createDate == "0" ? 0 : merged.createDate).local().format("YYYYMMDD");
    exportPDFLocal(installHtmlString, filename);
  }

  const siteSurveyGeneratePDF = async () => {
    const surveyDrawing = await getReportAttachs(infos);
    const text = await fetch(SiteSurveyPdf).then(r => r.text());
    const siteSurveyHtmlString = buildSiteSurveyReportHtml(text, infos, surveyDrawing, t);
    const ctry = infos.siteCountry ? getCountryCode(infos.siteCountry) : '';
    const sname = infos.siteSitename || '';
    const filename = "PR-" + ctry + "-" + sname + "-" + moment.utc(infos.createDate == "0" ? 0 : infos.createDate).local().format("YYYYMMDD");
    exportPDFLocal(siteSurveyHtmlString, filename);
  }

  const producthistoryGeneratePDF = async () => {
    const productHistoryHtmlString = '';
    let str = "";
    const companyCountry = "";
    let siteName = "";
    let filename = "";
    let data = {
      ...infos
    }

    const serviceReplacements = [];
    console.log(infos.serviceReplacements);
    infos.serviceReplacements.map(x => {
      serviceReplacements.push("<tr>" +
        "<td class='text-center'>" + moment.utc(x.registDate > 0 ? x.registDate : 0).local().format('YYYY-MM-DD') + "</td>" +
        "<td class='text-center'>" + x.warrantyYN + "</td>" +
        "<td class='text-center'>" + x.partscost + "</td>" +
        "<td class='text-center'>" + x.laborcost + "</td>" +
        "<td class='text-center'>" + x.vat + "</td>" +
        "<td class='text-center'>" + x.estimate + "</td>" +
        "<td class='text-center'>" + x.parts + "</td>" +
        "<td class='text-center'>" + x.etc + "</td>" +
        "<td class='text-center'>" + x.proofnumber + "</td>" +
        "</tr>");
    })

    const snhistorieList = [];
    infos.snhistorielist.map(x => {
      snhistorieList.push("<tr class 'text-center'>" +
        "<td>" + moment.utc(x.updated > 0 ? x.updated : 0).local().format('YYYY-MM-DD') + "</td>" +
        "<td>" + x.sn + "</td>" +
        "<td>" + x.user + "</td>" +
        "</tr>");
    })

    data = { ...data, snhistorylist: snhistorieList.join(""), partReplacements: serviceReplacements.join("") }

    siteName = infos.sitename ? infos.sitename : "";
    filename = "PR-" + infos.sn + "-" + siteName + "-" + moment().format('YYYYMMDD');
    fetch(produthistoryreport).then(response => response.text())
      .then(text => {
        str = text;
        const reV = /\{pr\.[a-z]+\}/gi;
        const foundV = str.match(reV);
        foundV.map(x => {
          const key = x.replace("{pr.", "").replace("}", "");
          if (data.hasOwnProperty(key)) {
            str = str.replaceAll(x, data[key] == null ? "" : data[key]);
          } else {
            str = str.replaceAll(x, "");
          }
        })

        const reC = /\{prCheck\.[a-z]+\}/gi;
        const foundC = str.match(reC);
        foundC.map(x => {
          const checkKey = x.replace("{prCheck.", "").replace("}", "");
          if (data.hasOwnProperty(checkKey)) {
            str = str.replaceAll(x, data[checkKey] == true ? "checked" : "");
          }
        })

        exportPDF_productHistory(str, filename);
      });
  }

  // 넘어온 리스트 데이터 확인 후 생성
  const generatePDF = () => {
    if (reportType == 'sitesurvey') {
      siteSurveyGeneratePDF();
    } else if (reportType == 'ccr') {
      ccrGeneratePDF();
    } else if (reportType == 'ProductHistory') {
      producthistoryGeneratePDF();
    }
    else {
      installGeneratePDF();
    }
  }

  const exportPDFLocal = (generatedHtml, filename) => {
    const opt = {
      margin: 0.4,
      filename: filename,
      image: { type: 'jpeg', quality: 0.98 }, // Exported image quality and format
      html2canvas: { scrollY: 0, scale: 3, useCORS: true }, // useCORS is very important to solve the problem of cross-domain images in the document
      jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait', useCORS: true },
    };
    if (generatedHtml) {
      runPdfExport(html2pdf().set(opt).from(generatedHtml).save(), 'ExportPdfReport.exportPDFLocal'); // export
    }
  }

  const exportPDF_productHistory = (generatedhtml, filename) => {
    const options = {
      margin: 0.4,
      filename: filename,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scrollY: 0, scale: 3, useCORS: true },
      jsPDF: { unit: 'in', format: 'a4', orientation: 'landscape', useCORS: true },
    };
    if (generatedhtml) {
      runPdfExport(html2pdf().set(options).from(generatedhtml).save(), 'ExportPdfReport.exportPDF_productHistory');
    }
  }

  return (
    <>
      <button className={css || "btn btn-primary btn-block btn-lg"} onClick={auto && generatePDF}>{t('io.button.reportExport')}
      </button>
    </>
  )
};

export const getReportAttachs = async (item) => {
  const attList = [];
  const apiPost = (body) => axios.post(AppConfig.API_SERVER_PATH + "/file/geturl", body, JWT());
  for (const x of item.attachments) {
    //if(x.path.toUpperCase().endsWith("JPG") || x.path.toUpperCase().endsWith("JPEG")){
    const url = await resolveFileUrl(x, apiPost);
    if (url) {
      if (x.type == "shock" || x.type == "tilt") {
        attList.push({ type: x.type, path: "<img src='" + url + "'  style='max-width:365px;max-height:270px;' class='img-fluid'  style='width: 100%;margin-bottom:16px;' crossorigin='anonymous' alt='" + escapeHtmlAttr(x.name) + "'/>" });
      } else if (x.type == "pano" || x.type == "ct" || x.type == "ceph") {
        attList.push({ type: x.type, path: "<img src='" + url + "'  style='max-width:90%;height:320px;' class='img-fluid'  style='width: 100%;margin-bottom:16px;' crossorigin='anonymous' alt='" + escapeHtmlAttr(x.name) + "'/>" });
      } else if (x.type == "doc") {
        attList.push({ type: x.type, path: "<img src='" + url + "'  style='max-width:880px;max-height:970px;' class='img-fluid'  style='width: 100%;margin-bottom:16px;' crossorigin='anonymous' alt='" + escapeHtmlAttr(x.name) + "'/>" });
      }
      else {
        attList.push("<img src='" + url + "'  style='height:370px;width:440px;' class='img-fluid'  style='width: 100%;margin-bottom:16px;' crossorigin='anonymous' alt='" + escapeHtmlAttr(x.name) + "'/>");
      }
    }
    //}
  }
  return attList;
}

// ExportPdfReportToEmail removed (BUG-052) — unused, violated Hook rules.

export const getAttachs = async (item) => {
  const attList = [];
  const apiPost = (body) => axios.post(AppConfig.API_SERVER_PATH + "/file/geturl", body, JWT());
  for (const x of item.attachments) {
    //if(x.path.toUpperCase().endsWith("JPG") || x.path.toUpperCase().endsWith("JPEG")){
    const url = await resolveFileUrl(x, apiPost);
    if (url) {
      attList.push("<img src='" + url + "' style='width: 100%;margin-bottom:16px;' crossorigin='anonymous' alt='" + escapeHtmlAttr(x.name) + "'/>");
    }
    //}
  }
  return attList;
}

export const ccrGeneratePDF = async (infos, t, filename = "") => {
  let str = '';
  //fetch(CCTPdfTest).then(response => response.text())
  //.then(text => {
  //  exportPDF("test", text);
  //});
  //return;
  //filename = "CSR-AU-RA71840033-Strathfield Medical Imaging -20210907";
  let companyCountry = "";
  let siteName = "";
  let data = {
    ...infos,
    spcountry: infos.company ? getCountryName(infos.company.country) : "",
    assigneeName: infos.assigneeInfo ? infos.assigneeInfo.firstName + " " + infos.assigneeInfo.lastName : "",
    assigneeInfoTel: infos.assigneeInfo ? infos.assigneeInfo.tel : ""
  };

  data = {
    ...data,
    companyname: infos.company ? infos.company.name : "",
    companycontactTel: infos.company ? infos.company.contactTel : "",
    companyaddress: infos.company ? ((infos.company.address || "") + " " + (infos.company.addressExtra || "")) : "",
  };
  companyCountry = infos.company ? infos.company.country : "";

  data = {
    ...data,
    siteName: infos.site ? infos.site.sitename : "",
    siteMobile: infos.site ? infos.site.customerHp : "",
    customerName: infos.site ? ((infos.site.customerFirstName || "") + " " + (infos.site.customerLastName || "")) : "",
    customerMobile: infos.site ? infos.site.customerHp : "",
    siteAddress: infos.site ? (infos.site.address + " " + infos.site.addressExtra) : "",
    siteZipCode: infos.site ? infos.site.zipcode : "",
    siteCountry: infos.site ? (infos.site.country ? getCountryName(infos.site.country) : "") : "",
  };
  siteName = infos.site ? infos.site.sitename : "";

  data = {
    ...data,
    createDate: moment.utc(infos.effectiveDate > 0 ? infos.effectiveDate : infos.createDate).local().format('YYYY-MM-DD'),
    servicecategory: infos.category ? (infos.category + " > " + infos.categoryDetail) : "",
  };

  const attList = await getAttachs(infos);
  filename = "CSR-" + companyCountry + "-" + infos.sn + "-" + siteName + "-" + moment().format('YYYYMMDD');

  const commentList = [];
  infos.comments.map(x => {
    commentList.push("<div style='padding: 8px 0px;'>" +
      "<span style='font-weight: bold;'>" + escapeHtml(x.creator) + "</span>&nbsp;/&nbsp;" +
      "<span>" + moment.utc(x.createDate).local().format('YYYY-MM-DD HH:mm:ss') + "</span><br/>" +
      "<span>" + escapeHtml(x.comment) + "</span>" +
      "</div>");
  })
  const partList = [];
  infos.replacements.map(x => {
    partList.push("<div>" +
      "<span>" + escapeHtml(x.title) + " &lt; " + escapeHtml(x.subtitle) + "</span><br/>" +
      ((x.oldsn || x.oldsn) ? "<span>" + escapeHtml(x.oldsn) + " &lt; " + escapeHtml(x.newsn) + "</span>" : "") +
      "</div>");
  })

  data = {
    ...data,
    attachments: attList.join("<br />"),
    engineersRemarks: infos.engineersRemarks || "",
    pendingReason: infos.pendingReason || "",
    sigpath: infos.sigpath || "",
    servicerendereds: commentList.join(""),
    partreplacements: partList.join(""),
  };
  fetch(CCTPdf).then(response => response.text())
    .then(text => {
      str = text;
      const reT = /\{tt\.[a-z]+\}/gi;
      const foundT = str.match(reT);
      foundT.map(x => {
        str = str.replaceAll(x, t(x.replace("{tt.", "").replace("}", "")))
      })

      const reV = /\{ir\.[a-z]+\}/gi;
      const foundV = str.match(reV);
      foundV.map(x => {
        const key = x.replace("{ir.", "").replace("}", "");
        if (data.hasOwnProperty(key)) {
          str = str.replaceAll(x, data[key]);
        }
      })
      str = blankUnfilledTokens(str, 'ir');
      console.log(str);

      exportPDF(filename, str);
    });
}

const exportPDF = (filename, generatedHtml) => {
  const opt = {
    margin: 0.4,
    filename: filename || "Exported PDF",
    image: { type: 'jpeg', quality: 0.98 }, // Exported image quality and format
    html2canvas: { scrollY: 0, scale: 3, useCORS: true }, // useCORS is very important to solve the problem of cross-domain images in the document
    jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait', useCORS: true },
  };
  if (generatedHtml) {
    runPdfExport(html2pdf().set(opt).from(generatedHtml).save(), 'ExportPdfReport.exportPDF'); // export
  }
}
